import { useEffect, useState } from "react";
import axios from "axios";
import { format, addDays } from "date-fns";
import { Building2, Video, Sun, Moon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { API, SERVICES, fmtTime } from "@/lib/api";

const TypeCard = ({ active, onClick, icon: Icon, title, text, testid, color }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`btn-pop rounded-[1.75rem] border-[3px] p-7 text-left ${active ? "border-slate-800 " + color : "border-slate-200 bg-white hover:border-teal-300"}`}>
    <Icon className="h-10 w-10 text-slate-800" />
    <p className="font-display mt-5 text-2xl font-semibold text-slate-800">{title}</p>
    <p className="mt-1 text-sm text-slate-600">{text}</p>
  </button>
);

export const StepType = ({ form, set }) => (
  <div className="grid gap-5 sm:grid-cols-2" data-testid="booking-wizard-step1">
    <TypeCard active={form.type === "clinic"} onClick={() => set({ type: "clinic" })} icon={Building2} color="bg-[#fff1f1]"
      title="In-clinic visit" text="Check-ups, cleaning & treatment at our Malad West clinic." testid="booking-type-inclinic" />
    <TypeCard active={form.type === "online"} onClick={() => set({ type: "online" })} icon={Video} color="bg-[#e0f2fe]"
      title="Online consultation" text="A video call with the doctor from home. Link shared by the clinic." testid="booking-type-online" />
  </div>
);

export const StepChild = ({ form, set }) => (
  <div className="space-y-6" data-testid="booking-wizard-step2">
    <div className="grid gap-4 sm:grid-cols-2">
      <div><Label>Child's name</Label><Input className="mt-2 h-12 rounded-xl" value={form.child_name} onChange={(e) => set({ child_name: e.target.value })} placeholder="e.g. Aarav" data-testid="booking-child-name-input" /></div>
      <div><Label>Child's age</Label><Input className="mt-2 h-12 rounded-xl" value={form.child_age} onChange={(e) => set({ child_age: e.target.value })} placeholder="e.g. 5 years" data-testid="booking-child-age-input" /></div>
    </div>
    <div>
      <Label>What do you need help with?</Label>
      <div className="mt-3 flex flex-wrap gap-2">
        {SERVICES.map((s, i) => (
          <button type="button" key={s} onClick={() => set({ service: s })} data-testid={`booking-service-${i}`}
            className={`rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors ${form.service === s ? "border-teal-600 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-teal-300"}`}>{s}</button>
        ))}
      </div>
    </div>
    <div><Label>Anything we should know? (optional)</Label><Textarea className="mt-2 rounded-xl" rows={3} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Symptoms, fears, allergies..." data-testid="booking-notes-input" /></div>
  </div>
);

const SlotGroup = ({ icon: Icon, label, slots, form, set }) => slots.length > 0 && (
  <div>
    <p className="flex items-center gap-2 text-sm font-bold text-slate-500"><Icon className="h-4 w-4" />{label}</p>
    <div className="mt-2 grid grid-cols-3 gap-2">
      {slots.map((t) => (
        <button type="button" key={t} onClick={() => set({ time: t })} data-testid="booking-slot-button"
          className={`rounded-xl border-2 py-2.5 text-sm font-bold transition-colors ${form.time === t ? "border-slate-800 bg-[#f5b82e] text-slate-900" : "border-slate-200 bg-white text-slate-700 hover:border-teal-400"}`}>{fmtTime(t)}</button>
      ))}
    </div>
  </div>
);

export const StepSchedule = ({ form, set }) => {
  const [slots, setSlots] = useState(null);
  const [blocked, setBlocked] = useState([]);
  useEffect(() => { axios.get(`${API}/blocked-dates`).then((r) => setBlocked(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!form.date) return;
    setSlots(null);
    axios.get(`${API}/slots`, { params: { date: form.date } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
  }, [form.date]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const disabled = (d) => d < today || d > addDays(today, 60) || d.getDay() === 0 || blocked.includes(format(d, "yyyy-MM-dd"));

  return (
    <div className="grid gap-8 md:grid-cols-[auto_1fr]" data-testid="booking-wizard-step3">
      <div className="rounded-2xl border border-slate-200 bg-white p-2" data-testid="booking-date-picker">
        <Calendar mode="single" selected={form.date ? new Date(`${form.date}T00:00:00`) : undefined}
          onSelect={(d) => d && set({ date: format(d, "yyyy-MM-dd"), time: "" })} disabled={disabled} />
      </div>
      <div className="space-y-5">
        {!form.date && <p className="rounded-2xl bg-[#fef7e5] p-5 text-sm font-semibold text-slate-700">Pick a day to see free times. We're open Mon–Sat.</p>}
        {form.date && slots === null && <p className="text-sm text-slate-500">Finding free slots...</p>}
        {slots && slots.length === 0 && <p className="rounded-2xl bg-[#fff1f1] p-5 text-sm font-semibold text-slate-700" data-testid="booking-no-slots">No free slots on this day. Please try another date.</p>}
        {slots && (
          <>
            <SlotGroup icon={Sun} label="Morning" slots={slots.filter((s) => s < "13:00")} form={form} set={set} />
            <SlotGroup icon={Moon} label="Evening" slots={slots.filter((s) => s >= "13:00")} form={form} set={set} />
          </>
        )}
      </div>
    </div>
  );
};

export const StepParent = ({ form, set }) => (
  <div className="grid gap-4 sm:grid-cols-2" data-testid="booking-wizard-step4">
    <div><Label>Parent's name</Label><Input className="mt-2 h-12 rounded-xl" value={form.parent_name} onChange={(e) => set({ parent_name: e.target.value })} data-testid="booking-parent-name-input" /></div>
    <div><Label>Mobile number</Label><Input className="mt-2 h-12 rounded-xl" type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="+91" data-testid="booking-phone-input" /></div>
    <div className="sm:col-span-2"><Label>Email (for confirmation)</Label><Input className="mt-2 h-12 rounded-xl" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="booking-email-input" /></div>
  </div>
);
